<template>
  <div class="page-content-container" ref="generatedReportsView" @scroll="onScroll">
    <div class="title-container mb-4">
      <h4 class="page-title">Generated Reports</h4>
      <p class="text-muted">
        Reports you requested. Download them here once they are ready.
      </p>
    </div>

    <div class="reports-content">
      <div class="header-actions">
        <div class="search-container">
          <i class="fas fa-search input-icon"></i>
          <input type="text" placeholder="Search by report name, batch ID or filters" v-model="searchQuery" @keyup.enter="loadReports()" class="search-input">
        </div>

        <select v-model="selectedReportType" @change="loadReports()" class="type-filter">
          <option value="">All Reports</option>
          <option v-for="reportTypeOption in reportTypeOptions" :key="reportTypeOption.value" :value="reportTypeOption.value">{{ reportTypeOption.label }}</option>
        </select>

        <button class="search-btn" :disabled="isLoadingReports" @click="loadReports()">
          <i class="fas fa-search me-2"></i>Search
        </button>
      </div>

      <div class="table-container">
        <SkeletonTableLoader v-if="isLoadingReports" :rowCount="7" :columnCount="6" />

        <div v-else-if="reports.length === 0" class="no-data-container">
          <p class="no-data-text">No reports found. Try adjusting your search or generate a report first.</p>
        </div>

        <table v-else>
          <thead>
            <tr>
              <th>ID</th>
              <th>Report</th>
              <th>Status</th>
              <th>Requested At</th>
              <th>Generated At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="report in reports" :key="report.id">
              <td>{{ report.id }}</td>
              <td>
                {{ generateReportTitle(report) }}
                <i v-if="reportFilterLines[report.id].length > 0" :id="`report-filters-${report.id}`" class="fas fa-info-circle filters-hint"></i>
                <b-tooltip v-if="reportFilterLines[report.id].length > 0" :target="`report-filters-${report.id}`" triggers="hover" placement="top">
                  <div class="filters-tooltip-content">
                    <strong class="filters-heading">Filters</strong>
                    <div v-for="filterLine in reportFilterLines[report.id]" :key="filterLine" class="filter-line">{{ filterLine }}</div>
                  </div>
                </b-tooltip>
              </td>
              <td>
                <div
                    :class="[
                      'status-tag',
                      {
                        warning: report.status.toLowerCase() === 'pending' || report.status.toLowerCase() === 'processing',
                        success: report.status.toLowerCase() === 'succeeded',
                        danger: report.status.toLowerCase() === 'failed'
                      },
                    ]"
                >
                  {{ report.status }}
                </div>
              </td>
              <td>{{ formatDate(report.requestedAt) }}</td>
              <td>{{ formatGeneratedAt(report) }}</td>
              <td>
                <div v-if="getReportActionMenu(report).length > 0" class="actions-dropdown" >
                  <button class="actions-btn" @click="toggleActionMenu(report.id)">
                    ACTIONS
                    <i class="fas fa-chevron-down" />
                  </button>
                  <div v-if="activeActionMenu === report.id" class="custom-dropdown-menu">
                    <button v-for="action in getReportActionMenu(report)" :key="action" class="action-option" @click="onReportActionPerformed(report, action)">
                      <i :class="getActionIcon(action)" />
                      {{ action }}
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="isLoadingMoreReports" class="loading-more-indicator">
          <div class="spinner"></div>
          <span>Loading more reports...</span>
        </div>

        <div v-if="!nextPageToken && reports.length > 0 && !isLoadingMoreReports && !isLoadingReports" class="all-fetched-indicator">
          <i class="fas fa-check-circle tick-icon"></i>
          <span>All reports have been loaded.</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import {mapActions} from "vuex";
import { formatToDDMMMYY_HHmmss } from "../../helpers/FormatHelpers";
import { ReportType } from "../../constants/reports";
import SkeletonTableLoader from '@/components/Shared/SkeletonTableLoader.vue';

export default {
  name: "GeneratedReports",
  components: {
    SkeletonTableLoader
  },
  data() {
    return {
      isLoadingReports: false,
      reports: [],
      searchQuery: '',
      selectedReportType: '',
      reportTypeOptions: [
        { value: ReportType.USER_CONTENT_ACTIVITY, label: "User Content Activity" },
        { value: ReportType.USER_ENROLLMENTS, label: "Users Enrollments" },
        { value: ReportType.USERS, label: "Users" }
      ],
      activeActionMenu: null,
      nextPageToken: null,
      isLoadingMoreReports: false,
      pageSize: 10,
      appliedSearchQuery: '',
      reportsRequestId: 0
    };
  },
  async created() {
    await this.loadReports();
  },
  mounted() {
    this.generatedReportsViewResizeObserver = new ResizeObserver(() => this.onScroll());
    this.generatedReportsViewResizeObserver.observe(this.$refs.generatedReportsView);
  },
  beforeDestroy() {
    this.generatedReportsViewResizeObserver.disconnect();
  },
  computed: {
    reportFilterLines() {
      const filterLinesByReportId = {};

      this.reports.forEach(report => {
        filterLinesByReportId[report.id] = this.getReportFilterLines(report);
      });

      return filterLinesByReportId;
    }
  },
  methods: {
    async loadReports() {
      this.reportsRequestId += 1;

      const requestId = this.reportsRequestId;

      try {
        this.isLoadingReports = true;
        this.nextPageToken = null;
        this.appliedSearchQuery = this.searchQuery.trim();

        const result = await this.fetchGeneratedReports({ pageSize: this.pageSize, type: this.selectedReportType, searchQuery: this.appliedSearchQuery });

        if (requestId !== this.reportsRequestId) {
          return;
        }

        this.reports = result.reports;
        this.nextPageToken = result.nextPageToken;

        this.$nextTick(() => this.onScroll());
      } catch (error) {
        console.error(error)
      } finally {
        if (requestId === this.reportsRequestId) {
          this.isLoadingReports = false;
        }
      }
    },
    ...mapActions("reports", ["fetchGeneratedReports"]),
    formatDate(date) {
      return formatToDDMMMYY_HHmmss(date);
    },
    formatGeneratedAt(report) {
      return report.completedAt ? formatToDDMMMYY_HHmmss(report.completedAt) : "N/A";
    },
    toggleActionMenu(id) {
      this.activeActionMenu = this.activeActionMenu === id ? null : id;
    },
    getActionIcon(action) {
      switch(action.toLowerCase()) {
        case 'download':
          return 'fas fa-download';
        case 'copy password':
          return 'fas fa-key';
        default:
          return 'fas fa-circle';
      }
    },
    generateReportTitle(report) {
      const parameters = JSON.parse(report.parameters || "{}");

      switch(report.type.toLowerCase()) {
        case ReportType.USER_CONTENT_ACTIVITY:
          return `User Content Activity Report for Batch ID: ${parameters.productVariantID}`;
        case ReportType.USER_ENROLLMENTS:
          return parameters.productVariantID ? `Users Enrollments Report for Batch ID: ${parameters.productVariantID}` : "Users Enrollments Report for All Batches";
        case ReportType.USERS:
          return "Users Report";
        default:
          return report.type;
      }
    },
    async onScroll() {
      const container = this.$refs.generatedReportsView;

      if (!container) {
        return;
      }

      const scrolledContainerHeight = container.scrollTop; // e.g: 1360 px already scrolled and not vissible
      const visibleContainerHeight = container.clientHeight; // e.g: 600 px is currently visible on screen
      const totalContainerHeight = container.scrollHeight; // e.g: full page is 2000 px
      const loadBeforeEndOffset = 50; // load more when only 50px is left more to load

      const seenContainerHeight = scrolledContainerHeight + visibleContainerHeight; // 1360 + 600 = 1960
      const loadMoreTriggerHeight = totalContainerHeight - loadBeforeEndOffset; // 2000 - 50 = 1950
      const isListEndReached = seenContainerHeight >= loadMoreTriggerHeight; // 1960 >= 1950 = true

      const shouldLoadMoreReports = isListEndReached && this.nextPageToken && !this.isLoadingMoreReports && !this.isLoadingReports;

      if (shouldLoadMoreReports) {
        await this.loadMoreReports();
      }
    },
    async loadMoreReports() {
      const requestId = this.reportsRequestId;

      try {
        this.isLoadingMoreReports = true;

        const result = await this.fetchGeneratedReports({ pageSize: this.pageSize, nextPageToken: this.nextPageToken, type: this.selectedReportType, searchQuery: this.appliedSearchQuery });

        if (requestId !== this.reportsRequestId) {
          return;
        }

        this.reports = this.reports.concat(result.reports);
        this.nextPageToken = result.nextPageToken;

        this.$nextTick(() => this.onScroll());
      } catch (error) {
        console.error(error)
      } finally {
        this.isLoadingMoreReports = false;
      }
    },
    getReportFilterLines(report) {
      const parameters = JSON.parse(report.parameters || "{}");

      switch(report.type.toLowerCase()) {
        case ReportType.USER_CONTENT_ACTIVITY:
          return [
            parameters.selectedUserIDs?.length ? `Users: ${parameters.selectedUserIDs.length} selected` : null,
            parameters.selectedContentTypes?.length ? `Content types: ${parameters.selectedContentTypes.join(", ")}` : null,
            parameters.selectedEnrollmentStatuses?.length ? `Enrollment status: ${parameters.selectedEnrollmentStatuses.join(", ")}` : null
          ].filter(Boolean);
        case ReportType.USERS:
          return [
            parameters.type ? `Type: ${parameters.type}` : null,
            parameters.status ? `Status: ${parameters.status}` : null,
            parameters.searchString ? `Search: ${parameters.searchString}` : null
          ].filter(Boolean);
        default:
          return [];
      }
    },
    getReportActionMenu(report) {
      const actions = [];

      if (report.fileUrl) {
        actions.push("Download");
      }

      if (report.filePassword) {
        actions.push("Copy password");
      }

      return actions;
    },
    onReportActionPerformed(report, action) {
      this.activeActionMenu = null;

      switch(action.toLowerCase()) {
        case 'download':
          this.downloadReport(report);
          break;
        case 'copy password':
          this.copyReportPassword(report);
          break;
        default:
          break;
      }
    },
    async downloadReport(report) {
      const response = await fetch(report.fileUrl);

      if (!response.ok) {
        this.$toasted.error(
            `Error while downloading file (${response.status})`,
            {
              theme: "outline",
              position: "bottom-center",
              duration: 3000,
            }
        );

        throw new Error(`Download failed: ${response.status}`);
      }

      const blob = await response.blob();

      const filename = report.fileName || report.fileUrl.split("/").pop().split("?")[0] || "download";

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      URL.revokeObjectURL(link.href);

      let isPasswordCopiedToClipboard = false;

      try {
        await navigator.clipboard.writeText(report.filePassword);
        isPasswordCopiedToClipboard = true;
      } catch (err) {
        isPasswordCopiedToClipboard = false;
        console.error('Failed to copy text: ', err);
      }

      this.$toasted.success(isPasswordCopiedToClipboard ? "File downloaded. Password copied to clipboard." : "File downloaded. Use Copy password in the row to get the password.", {
        icon: "check",
        theme: "outline",
        position: "bottom-center",
        duration: 5000,
      });
    },
    async copyReportPassword(report) {
      try {
        await navigator.clipboard.writeText(report.filePassword);
        this.$toasted.success("Password copied.", {
          icon: "check",
          theme: "outline",
          position: "bottom-center",
          duration: 3000,
        });
      } catch (err) {
        this.$toasted.show(`Password: ${report.filePassword}`, {
          theme: "outline",
          position: "bottom-center",
          duration: null,
          action: [
            {
              text: 'Dismiss',
              onClick: (e, toastObject) => {
                toastObject.goAway(0);
              }
            }
          ]
        });
      }
    }
  }
}
</script>

<style scoped>
.page-content-container {
  background-color: #f5f5f5;
  height: calc(100vh - var(--header-height));
  padding: 1.875rem;
  overflow-y: auto;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  justify-content: space-between;
}

.search-container {
  position: relative;
  flex: 1;
}

.input-icon {
  position: absolute;
  top: 50%;
  left: 0.75rem;
  transform: translateY(-50%);
  color: #98a2b3;
  font-size: 0.875rem;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 2.75rem;
  box-sizing: border-box;
  padding: 0 0.75rem 0 2.25rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  font-size: 0.875rem;
}

.table-container {
  margin-bottom: 1.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th,
td {
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

th {
  background-color: #f9fafb;
  font-weight: 500;
  color: #374151;
}

.actions-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background-color: white;
  font-size: 0.875rem;
  cursor: pointer;
}

.actions-dropdown {
  position: relative;
}

.custom-dropdown-menu {
  position: absolute;
  left: 0;
  top: 100%;
  margin-top: 0.25rem;
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 999;
  min-width: 160px;
  overflow: visible;
}

.action-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: #374151;
  width: 100%;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
}

.action-option:hover {
  background-color: #f9fafb;
}

.type-filter {
  height: 2.75rem;
  box-sizing: border-box;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0 0.75rem;
  font-size: 0.875rem;
  color: #344054;
  background: #ffffff;
  cursor: pointer;
}

.search-btn {
  display: inline-flex;
  align-items: center;
  height: 2.75rem;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  padding: 0 1rem;
  font-size: 0.875rem;
  white-space: nowrap;
  color: #344054;
  cursor: pointer;
}

.search-btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.loading-more-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  font-size: 0.9rem;
  color: #555;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #ddd;
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.all-fetched-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  color: #10b981;
  font-size: 0.95rem;
  font-weight: 500;
}

.tick-icon {
  font-size: 1.1rem;
  color: #10b981;
}

.filters-tooltip-content {
  text-align: left;
}

.filters-heading {
  display: block;
  margin-bottom: 0.375rem;
}

.filter-line + .filter-line {
  margin-top: 0.25rem;
}

.filters-hint {
  margin-left: 0.375rem;
  color: #9ca3af;
  cursor: help;
}

.no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  color: #333;
  text-align: center;
  margin-top: 10px;
}

.no-data-text {
  font-size: 16px;
  font-weight: 500;
}
</style>

